// When a photo was actually taken, from the JPEG's EXIF data: phones keep it
// in DateTimeOriginal. Files picked from a gallery often carry today's date
// as their "last modified", so without this a month of photos all lands on
// the day you uploaded them.

/** EXIF "YYYY:MM:DD HH:MM:SS" (camera-local time) → epoch ms, or undefined. */
export function exifTakenAt(buf: ArrayBuffer): number | undefined {
  const v = new DataView(buf);
  if (v.byteLength < 4 || v.getUint16(0) !== 0xffd8) return undefined; // not a JPEG
  let off = 2;
  while (off + 4 <= v.byteLength) {
    if (v.getUint8(off) !== 0xff) return undefined;
    const marker = v.getUint8(off + 1);
    const size = v.getUint16(off + 2);
    if (marker === 0xe1 && off + 10 <= v.byteLength && v.getUint32(off + 4) === 0x45786966) return readExif(v, off + 10, size - 8);
    if (marker === 0xda || marker === 0xd9) return undefined; // image data starts: no EXIF
    off += 2 + size;
  }
  return undefined;
}

function readExif(v: DataView, start: number, length: number): number | undefined {
  const end = Math.min(v.byteLength, start + length);
  if (start + 8 > end) return undefined;
  const little = v.getUint16(start) === 0x4949;
  const u16 = (o: number) => v.getUint16(o, little);
  const u32 = (o: number) => v.getUint32(o, little);
  const ascii = (o: number, n: number) => {
    let s = '';
    for (let i = 0; i < n && o + i < end; i++) {
      const c = v.getUint8(o + i);
      if (!c) break;
      s += String.fromCharCode(c);
    }
    return s;
  };
  /** Scan one IFD: the tags we want, and where the Exif sub-IFD is. */
  const ifd = (at: number) => {
    const found: { original?: string; plain?: string; exif?: number } = {};
    if (at + 2 > end) return found;
    const n = u16(at);
    for (let i = 0; i < n; i++) {
      const e = at + 2 + i * 12;
      if (e + 12 > end) break;
      const tag = u16(e);
      const valueAt = start + u32(e + 8);
      if (tag === 0x8769) found.exif = start + u32(e + 8);
      else if (tag === 0x9003) found.original = ascii(valueAt, 19);
      else if (tag === 0x0132) found.plain = ascii(valueAt, 19);
    }
    return found;
  };
  const ifd0 = ifd(start + u32(start + 4));
  const sub = ifd0.exif ? ifd(ifd0.exif) : {};
  return parseExifDate(sub.original ?? ifd0.plain);
}

export function parseExifDate(s: string | undefined): number | undefined {
  const m = s && /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/.exec(s);
  if (!m) return undefined;
  const [, y, mo, d, h, mi, se] = m.map(Number);
  if (y < 2000 || mo < 1 || mo > 12) return undefined;
  // Camera-local time; the phone and the household are both in the UK.
  const t = new Date(y, mo - 1, d, h, mi, se).getTime();
  return Number.isFinite(t) ? t : undefined;
}

/**
 * The best date for a photo: EXIF if it's believable, else the file's own
 * date if that's believable, else now. "Believable" = not in the future and
 * not before he was born.
 */
export function bestPhotoDate(exif: number | undefined, lastModified: number | undefined, now: number, notBefore?: number): number {
  const ok = (t: number | undefined): t is number => !!t && t <= now + 60_000 && (!notBefore || t >= notBefore);
  if (ok(exif)) return exif;
  if (ok(lastModified)) return lastModified;
  return now;
}
